// 2026-11-02 - FormuLearning - 조성 표시 규칙 공용 함수 (이슈 #101, M1002-02·09)
// 파일 위치: frontend/src/lib/composition.ts
// 목적: 성분표·칩·추천 카드가 같은 규칙으로 조성을 보이게 한다.
//       ① non-weight(`selected(non-weight)`)는 **mg** 로만 보이고 % 합계에서 뺀다(REQ-FN-3-02, M1002-02)
//       ② 질량% 는 **소수점 둘째 자리**(M1002-09). 중량 성분 합이 100 ± 0.1%p 안이면 표시값에 최대 잉여
//          보정을 걸어 표시 합이 정확히 100.00 이 되게 한다. 밖이면 보정하지 않는다 — 이상 데이터를 가리지 않는다.
//       저장 정밀도(셋째 자리, M84-09)는 바꾸지 않는다 — 표시 전용이다.
//       근거: `ai-agent/claude/261102_#101_S3조성합결함/작업계획_261102_S3조성합결함.md` §5.2 D3·D4
// 2026-11-03 수정 - 이슈 #102: ③ 성분 순서 = Step 3 X 순서(`orderByXVariables`, M1002-07) ④ User Define 초기 설계의
//                   X 빈칸 판정(`incompleteInitialBatches`, M1002-05) — 서버 `x_order`·`incomplete_initial_x` 와 같은 규칙.
//                   근거: `ai-agent/claude/261103_#102_S3UserDefine·처방표순서/설계_261103_#102 분석·구현설계.md` §2 D6·D8

import type { LotBatch } from '../api/lot'
import type { XVariable } from '../api/step3'

/** 학습 검사(`backend/app/step4/mobo/adapter.py` `COMPOSITION_SUM_TOL_PCT`)와 같은 허용치. */
export const COMPOSITION_SUM_TOL_PCT = 0.1

export const NON_WEIGHT_MODE = 'selected(non-weight)'

/** balance 시스템 하한(BR-S3-03) — 생성기 `BALANCE_MIN_PCT`·MOBO `BALANCE_LOWER_PCT` 와 같은 값. */
export const BALANCE_MIN_PCT = 1

/** 성분명 → X 변수. 표기 흔들림(대소문자·앞뒤 공백)을 정규화해 맞춘다. */
export function xVariableIndex(xVariables: XVariable[] | null | undefined): (name: string) => XVariable | undefined {
  const m = new Map<string, XVariable>()
  for (const x of xVariables ?? []) if (x.excipient) m.set(x.excipient.trim().toLowerCase(), x)
  return (name: string) => m.get(name.trim().toLowerCase())
}

export interface PctDisplay {
  /** 중량 성분별 표시 문자열(둘째 자리, 보정 반영). non-weight 키는 없다. */
  shown: Record<string, string>
  /** 중량 성분 실제 합(저장값 기준). 값이 하나도 없으면 null. */
  sum: number | null
  /** 합계 표시 문자열 — 보정되면 `100.00`. */
  sumShown: string
  /** 합이 100 ± 허용치 안인가. */
  sumOk: boolean
}

/**
 * 중량 성분 % 표시값 — 둘째 자리 + 합계 100.00 보정(최대 잉여법).
 * `isNonWeight` 가 참인 키는 표시·합계에서 뺀다.
 */
export function pctDisplay(
  values: Record<string, unknown>,
  isNonWeight: (name: string) => boolean,
): PctDisplay {
  const entries = Object.entries(values).filter(
    (e): e is [string, number] => !isNonWeight(e[0]) && typeof e[1] === 'number' && Number.isFinite(e[1]),
  )
  if (entries.length === 0) return { shown: {}, sum: null, sumShown: '—', sumOk: false }
  const sum = entries.reduce((s, [, v]) => s + v, 0)
  const sumOk = Math.abs(sum - 100) <= COMPOSITION_SUM_TOL_PCT + 1e-9
  // 0.01 단위 정수로 다룬다. 보정하지 않을 때는 그냥 반올림한다.
  const cents = entries.map(([k, v]) => ({ k, exact: v * 100, c: Math.round(v * 100) }))
  if (sumOk) {
    for (const e of cents) e.c = Math.floor(e.exact + 1e-9)
    let diff = 10000 - cents.reduce((s, e) => s + e.c, 0)
    const byRemainder = [...cents].sort((a, b) => (b.exact - b.c) - (a.exact - a.c))
    for (let i = 0; diff !== 0 && byRemainder.length > 0; i++) {
      if (diff > 0) {
        byRemainder[i % byRemainder.length].c += 1
        diff -= 1
      } else {
        const e = byRemainder[byRemainder.length - 1 - (i % byRemainder.length)]
        if (e.c > 0) { e.c -= 1; diff += 1 }
      }
    }
  }
  const shown: Record<string, string> = {}
  for (const e of cents) shown[e.k] = (e.c / 100).toFixed(2)
  const sumShown = sumOk ? '100.00' : sum.toFixed(2)
  return { shown, sum, sumShown, sumOk }
}

/** mg 표시(셋째 자리, 뒤 0 제거) — 총중량이 없으면 null. */
export function pctToMgText(pct: unknown, totalWeightMg: number | null | undefined): string | null {
  if (typeof pct !== 'number' || !Number.isFinite(pct) || !totalWeightMg || totalWeightMg <= 0) return null
  return String(Math.round(((pct * totalWeightMg) / 100) * 1000) / 1000)
}

/**
 * 성분명을 **Step 3 X 순서**(`sort_order`)로 정렬한다(#102 D8). 표기 흔들림은 정규화해 맞추고,
 * Step 3 에 없는 키는 뒤에 두되 들어온 순서를 지킨다. 보고서 `backend/app/report/builders.py` `x_order` 와 같다.
 */
export function orderByXVariables(names: string[], xVariables: XVariable[] | null | undefined): string[] {
  const rank = new Map<string, number>()
  ;[...(xVariables ?? [])]
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .forEach((x, i) => {
      const k = (x.excipient ?? '').trim().toLowerCase()
      if (k && !rank.has(k)) rank.set(k, i)
    })
  const r = (n: string) => rank.get(n.trim().toLowerCase())
  return names
    .map((n, i) => ({ n, i }))
    .sort((a, b) => {
      const ra = r(a.n), rb = r(b.n)
      if (ra !== undefined && rb !== undefined) return ra - rb
      if (ra !== undefined) return -1
      if (rb !== undefined) return 1
      return a.i - b.i
    })
    .map((e) => e.n)
}

/** User Define 초기 설계에서 비울 수 있는 칸인가(#102 D4) — 서버 `save_batch_composition` 과 같은 조건. */
export function canClearComposition(batch: LotBatch, runsAlgorithm: string | null | undefined): boolean {
  return batch.batch_type === 'init' && runsAlgorithm === 'user_defined'
}

/**
 * X 값이 빈 init 배치(#102 D6) — 계산 제외 배치는 뺀다. 대상은 Step 3 X 중 이름이 있는 행 전부이며
 * (balance 포함 — 중량 성분이 다 차면 서버가 채운다), 값이 숫자가 아니면 빈칸이다.
 */
export function incompleteInitialBatches(batches: LotBatch[], xVariables: XVariable[] | null | undefined): LotBatch[] {
  const names = (xVariables ?? []).map((x) => (x.excipient ?? '').trim()).filter(Boolean)
  return batches.filter((b) => {
    if (b.batch_type !== 'init' || b.is_excluded) return false
    // 이름은 서버 학습(`batch_x`)처럼 그대로 맞춘다 — 게이트 판정이 서버와 갈리지 않게 한다.
    const xv: Record<string, unknown> = b.x_values ?? {}
    return names.some((n) => {
      const v = xv[n]
      return typeof v !== 'number' || !Number.isFinite(v)
    })
  })
}
